/**
 * API Service для работы с backend
 * Централизованный класс для всех API запросов
 */
class ApiService {
  constructor() {
    this.baseURL = 'https://vpnch1k.ru/api/v1';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Базовый метод для выполнения HTTP запросов
   * @param {string} endpoint - Конечная точка API
   * @param {object} options - Опции запроса (method, headers, body)
   * @returns {Promise<object>} - Результат запроса
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      method: 'GET',
      ...options,
      headers: { ...this.defaultHeaders, ...options.headers },
    };

    try {
      
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error(`Ошибка запроса к ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Валидация пользователя через API
   * @param {object} userData - Данные пользователя для валидации
   * @param {number} userData.user_id - ID пользователя в Telegram
   * @param {string} userData.first_name - Имя пользователя
   * @param {string} userData.username - Username пользователя
   * @param {string} userData.photo_url - URL фото пользователя
   * @param {string} userData.start_param - Start параметр для реферальной программы
   * @returns {Promise<object>} - Результат валидации
   */
  async validateUser(userData) {
    
    return this.request('/users/validate', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Получение списка операций пользователя
   * @param {number} userId - ID пользователя
   * @returns {Promise<object>} - Список операций
   */
  async getUserOperations(userId) {
    return this.request(`/users/${userId}/operations`);
  }

  /**
   * Получение списка подписок пользователя
   * @param {number} userId - ID пользователя
   * @returns {Promise<object>} - Список подписок
   */
  async getUserSubscriptions(userId) {
    try {
      const response = await this.request(`/users/${userId}/subscriptions`);
      console.log('API ответ для подписок:', response);
      return response;
    } catch (error) {
      console.error(`Ошибка получения подписок для пользователя ${userId}:`, error);
      
      // Если это ошибка 404 (пользователь не найден), возвращаем пустой список
      if (error.message.includes('404')) {
        return { subscriptions: [], total_count: 0 };
      }
      
      throw error;
    }
  }

  /**
   * Получение деталей подписки
   * @param {number} subscriptionId - ID подписки
   * @returns {Promise<object>} - Детали подписки
   */
  async getSubscriptionDetails(subscriptionId) {
    return this.request(`/subscriptions/${subscriptionId}`);
  }

  /**
   * Покупка ключа
   * @param {object} purchaseData - Данные для покупки
   * @returns {Promise<object>} - Результат покупки
   */
  async purchaseKey(purchaseData) {
    return this.request('/purchase/key', {
      method: 'POST',
      body: JSON.stringify(purchaseData),
    });
  }

  /**
   * Обновление темы пользователя
   * @param {number} userId - ID пользователя
   * @param {string} theme - Новая тема (dark/light)
   * @returns {Promise<object>} - Результат обновления
   */
  async updateUserTheme(userId, theme) {
    
    return this.request(`/users/${userId}/theme`, {
      method: 'PATCH',
      body: JSON.stringify({ theme_mode: theme }),
    });
  }

  /**
   * Обновление языка пользователя
   * @param {number} userId - ID пользователя
   * @param {string} language - Новый язык (ru/en)
   * @returns {Promise<object>} - Результат обновления
   */
  async updateUserLanguage(userId, language) {
    return this.request(`/users/${userId}/language`, {
      method: 'PATCH',
      body: JSON.stringify({ language }),
    });
  }

  /**
   * Создание новой VPN подписки
   * @param {object} subscriptionData - Данные для создания подписки
   * @param {number} subscriptionData.user_id - ID пользователя
   * @param {number} subscriptionData.server_id - ID сервера
   * @param {number} subscriptionData.subscription_period - Период подписки в днях
   * @param {number} subscriptionData.traffic_gb - Объём трафика в ГБ (по умолчанию 50)
   * @returns {Promise<object>} - Созданная подписка
   */
  async createSubscription(subscriptionData) {
    const data = {
      user_id: subscriptionData.user_id,
      server_id: subscriptionData.server_id,
      subscription_period: subscriptionData.subscription_period,
      traffic_gb: subscriptionData.traffic_gb || 50,
      bonus_used: subscriptionData.bonus_used || 0
    };
    
    return this.request('/subscriptions/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Удаление VPN подписки
   * @param {object} deleteData - Данные для удаления подписки
   * @param {number} deleteData.subscription_id - ID подписки
   * @param {number} deleteData.user_id - ID пользователя
   * @returns {Promise<object>} - Результат удаления
   */
  async deleteSubscription(deleteData) {
    const data = {
      subscription_id: deleteData.subscription_id,
      user_id: deleteData.user_id
    };
    
    return this.request('/subscriptions/delete', {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  }

  /**
   * Создание платежа через ЮКасса или Stripe (LavaTop)
   * @param {object} paymentData - Данные для создания платежа
   * @param {number} paymentData.user_id - ID пользователя
   * @param {number} paymentData.server_id - ID сервера
   * @param {string} paymentData.period - Период подписки ('week', 'month' или 'year')
   * @param {number} paymentData.bonus_used - Использованные бонусы в копейках
   * @param {string} paymentData.payment_method - Способ оплаты ('yookassa' или 'stripe')
   * @param {number} [paymentData.subscription_id] - ID подписки для продления (опционально)
   * @returns {Promise<object>} - Данные платежа с URL для оплаты
   */
  async createPayment(paymentData) {
    const data = {
      user_id: paymentData.user_id,
      server_id: paymentData.server_id,
      period: paymentData.period,
      bonus_used: paymentData.bonus_used || 0,
      payment_method: paymentData.payment_method || 'yookassa',
      subscription_id: paymentData.subscription_id || null
    };
    
    return this.request('/payments/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Получение списка платежей пользователя
   * @param {number} userId - ID пользователя
   * @returns {Promise<object>} - Список платежей пользователя
   */
  async getUserPayments(userId) {
    try {
      const response = await this.request(`/payments/user/${userId}`);
      console.log('API ответ для платежей:', response);
      return response;
    } catch (error) {
      console.error(`Ошибка получения платежей для пользователя ${userId}:`, error);
      
      // Если это ошибка 404 (пользователь не найден), возвращаем пустой список
      if (error.message.includes('404')) {
        return [];
      }
      
      throw error;
    }
  }

  /**
   * Получение списка доступных серверов
   * @returns {Promise<object>} - Список серверов
   */
  async getServers() {
    try {
      const response = await this.request('/servers/');
      console.log('API ответ для серверов:', response);
      return response;
    } catch (error) {
      console.error('Ошибка получения серверов:', error);
      
      // В случае ошибки возвращаем пустой список
      return { servers: [], total_count: 0 };
    }
  }

  // ===== АДМИН МЕТОДЫ =====

  /**
   * Получение списка пользователей для админ-панели
   * @param {number} adminUserId - ID администратора
   * @param {number} page - Номер страницы
   * @param {number} limit - Количество пользователей на странице
   * @param {string} search - Поисковый запрос
   * @returns {Promise<object>} - Список пользователей с пагинацией
   */
  async getAdminUsersList(adminUserId, page = 1, limit = 50, search = '') {
    const params = new URLSearchParams({
      admin_user_id: adminUserId,
      page: page,
      limit: limit
    });

    if (search) {
      params.append('search', search);
    }

    return this.request(`/admin/users?${params.toString()}`);
  }

  /**
   * Получение профиля пользователя для админ-панели
   * @param {number} userId - ID пользователя
   * @param {number} adminUserId - ID администратора
   * @returns {Promise<object>} - Профиль пользователя с подписками
   */
  async getAdminUserProfile(userId, adminUserId) {
    const params = new URLSearchParams({
      admin_user_id: adminUserId
    });

    return this.request(`/admin/users/${userId}?${params.toString()}`);
  }

  /**
   * Обновление цен сервера
   * @param {number} serverId - ID сервера
   * @param {number} adminUserId - ID администратора
   * @param {object} pricingData - Данные с ценами
   * @param {number} pricingData.price_week - Цена за неделю
   * @param {number} pricingData.price_month - Цена за месяц
   * @returns {Promise<object>} - Обновленный сервер
   */
  async updateServerPricing(serverId, adminUserId, pricingData) {
    const params = new URLSearchParams({
      admin_user_id: adminUserId
    });

    return this.request(`/admin/servers/${serverId}/pricing?${params.toString()}`, {
      method: 'PATCH',
      body: JSON.stringify(pricingData),
    });
  }

  /**
   * Удаление подписки администратором
   */
  async adminDeleteSubscription(subscriptionId, targetUserId, adminUserId) {
    const data = {
      subscription_id: subscriptionId,
      target_user_id: targetUserId,
      admin_user_id: adminUserId
    };

    return this.request(`/admin/subscriptions/${subscriptionId}`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  }

  // ===== ПАРТНЁРСКАЯ ПРОГРАММА =====

  async getReferralSettings() {
    return this.request('/referral/settings');
  }

  async getReferralStats(userId, period = 'month', dateFrom = null, dateTo = null) {
    const params = new URLSearchParams({ period });
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    return this.request(`/referral/stats/${userId}?${params.toString()}`);
  }

  async createWithdrawal(withdrawalData) {
    return this.request('/referral/withdraw', {
      method: 'POST',
      body: JSON.stringify(withdrawalData),
    });
  }

  async getUserWithdrawals(userId) {
    return this.request(`/referral/withdrawals/${userId}`);
  }

  // ===== ADMIN: ПАРТНЁРСКАЯ ПРОГРАММА =====

  async getAdminReferralSettings(adminUserId) {
    return this.request(`/admin/referral-settings?admin_user_id=${adminUserId}`);
  }

  async updateAdminReferralSettings(adminUserId, data) {
    return this.request(`/admin/referral-settings?admin_user_id=${adminUserId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getAdminWithdrawals(adminUserId, status = null) {
    const params = new URLSearchParams({ admin_user_id: adminUserId });
    if (status) params.append('status', status);
    return this.request(`/admin/withdrawals?${params.toString()}`);
  }
}

// Создаем единственный экземпляр (Singleton)
const apiService = new ApiService();

export default apiService; 